import React, { lazy, Suspense, useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ConfigProvider } from "antd";
import enUS from "antd/es/locale/en_US";
import deDE from "antd/es/locale/de_DE";
import dayjs from "dayjs";
import "dayjs/locale/de";

import { SentryRoutes } from "./Config";
import PrivateRoute from "./AppPrivateRoutes";
import Loader from "./Components/UI/Loader";

import { setIsUserRoleBaseUrl } from "./Store/Reducers/AuthModule/auth";
import { RootState } from "./Store/store";
import { setCallMenuList } from "./Store/Reducers/sideMenuBar";

import useFetchGeneralSettings from "./Hooks/useFetchGeneralSettings";

import {
  capitalizeFirstLetter,
  getToLocalStorage,
  setToLocalStorage} from "./Library/Utils";
import { routeHandlers } from "./Library/Utils/routeHandlers";
import useManagerPagePermission from "./Hooks/useManagerPagePermission";

const CommonComponent = lazy(() => import("./Pages/Common"));

const SignIn = lazy(() => import("./Pages/AuthModule/SignIn"));
const NoCompany = lazy(() => import("./Pages/AuthModule/NoCompany"));
const NotFound = lazy(() => import("./Pages/404"));
const InvitedSignUp = lazy(
  () => import("./Pages/AuthModule/SignUp/invitedSignUp")
);
const ForgotPassword = lazy(() => import("./Pages/AuthModule/ForgotPassword"));
const ResetPassword = lazy(() => import("./Pages/AuthModule/ResetPassword"));
const VerifyUser = lazy(() => import("./Pages/AuthModule/SignUp/verifyUser"));

export interface IAppRoutesProps {}

const AppRoutes = () => {
  const dispatch = useDispatch();

  const userDetails = useSelector(
    (state: RootState) => state.authReducer.userDetails
  );

  const generalSettings = useFetchGeneralSettings();
  useManagerPagePermission();
  const [locale, setLocale] = useState(enUS);

  useEffect(() => {
    // localStorage["language"] is the single source of truth for the active language.
    // We intentionally do not fall back to `generalSettings.default_language` here —
    // that is a company-wide setting, not the individual user's preference.
    const fromStorage = getToLocalStorage("language");
    const fromProfile = capitalizeFirstLetter(userDetails?.preferred_language);
    const localLanguage = fromStorage ?? fromProfile ?? "De";

    dayjs.locale(localLanguage.toLowerCase());
    setLocale(capitalizeFirstLetter(localLanguage) === "En" ? enUS : deDE);
    dispatch(setCallMenuList(true));
  }, [userDetails?.preferred_language]);

  return (
    <ConfigProvider locale={locale}>
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <SentryRoutes>
            <Route path="/" element={<PrivateRoute />}>
              <Route
                path={routeHandlers.Dashboard.url}
                element={<CommonComponent componentName="Dashboard" />}
              />
              <Route
                path={routeHandlers.MyReports.url}
                element={<CommonComponent componentName="MyReports" />}
              />
              <Route
                path={routeHandlers.MyReportDetail.url}
                element={<CommonComponent componentName="MyReportDetail" />}
              />
              <Route
                path={routeHandlers.MeetingSchedule.url}
                element={<CommonComponent componentName="MeetingSchedule" />}
              />
              <Route
                path={routeHandlers.MeetingScheduleDetail.url}
                element={
                  <CommonComponent componentName="MeetingScheduleDetail" />
                }
              />
              <Route
                path={routeHandlers.Archive.url}
                element={<CommonComponent componentName="Archive" />}
              />
              <Route
                path={routeHandlers.RadicalFocusSetting.url}
                element={
                  <CommonComponent componentName="RadicalFocusSetting" />
                }
              />
              <Route
                path={routeHandlers.InitiativeTracking.url}
                element={<CommonComponent componentName="InitiativeTracking" />}
              />
              <Route
                path={routeHandlers.KpiReporting.url}
                element={<CommonComponent componentName="KpiReporting" />}
              />
              <Route
                path={routeHandlers.KpiDetail.url}
                element={<CommonComponent componentName="KpiDetail" />}
              />
              <Route
                path={routeHandlers.ObjectiveOverview.url}
                element={<CommonComponent componentName="ObjectiveOverview" />}
              />
              <Route
                path={routeHandlers.ActivityOverview.url}
                element={<CommonComponent componentName="ActivityOverview" />}
              />
              <Route
                path={routeHandlers.ProcessOverview.url}
                element={<CommonComponent componentName="ProcessOverview" />}
              />
              <Route
                path={routeHandlers.ForeCast.url}
                element={<CommonComponent componentName="ForeCast" />}
              />
              <Route
                path={routeHandlers.BusinessModel.url}
                element={<CommonComponent componentName="BusinessModel" />}
              />
              <Route
                path={routeHandlers.Market.url}
                element={<CommonComponent componentName="Market" />}
              />
              <Route
                path={routeHandlers.Brand.url}
                element={<CommonComponent componentName="Brand" />}
              />
              <Route
                path={routeHandlers.CustomerFocus.url}
                element={<CommonComponent componentName="CustomerFocus" />}
              />
              <Route
                path={routeHandlers.CustomerSegmentation.url}
                element={
                  <CommonComponent componentName="CustomerSegmentation" />
                }
              />
              <Route
                path={routeHandlers.OpsModel.url}
                element={<CommonComponent componentName="OpsModel" />}
              />
              <Route
                path={routeHandlers.PeopleCluture.url}
                element={<CommonComponent componentName="PeopleCluture" />}
              />
              <Route
                path={routeHandlers.FinanceReporting.url}
                element={<CommonComponent componentName="FinanceReporting" />}
              />
              <Route
                path={routeHandlers.Planning.url}
                element={<CommonComponent componentName="Planning" />}
              />
              <Route
                path={routeHandlers.Feedback.url}
                element={<CommonComponent componentName="Feedback" />}
              />
              <Route
                path={routeHandlers.ValuesAndPrinciples.url}
                element={
                  <CommonComponent componentName="ValuesAndPrinciples" />
                }
              />
              <Route
                path={routeHandlers.FeedbackSetting.url}
                element={<CommonComponent componentName="FeedbackSetting" />}
              />
              <Route
                path={routeHandlers.Reviews.url}
                element={<CommonComponent componentName="Reviews" />}
              />
              <Route
                path={routeHandlers.ReviewSetting.url}
                element={<CommonComponent componentName="ReviewSetting" />}
              />
              <Route
                path={routeHandlers.SuccessManager.url}
                element={<CommonComponent componentName="SuccessManager" />}
              />
              <Route
                path={routeHandlers.ProfileSettings.url}
                element={<CommonComponent componentName="ProfileSettings" />}
              />
            </Route>
            <Route path="/invited/signup/:token" element={<InvitedSignUp />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/no-company" element={<NoCompany />} />
            <Route path="/forgot/password" element={<ForgotPassword />} />
            <Route path="/reset/password/:token" element={<ResetPassword />} />
            <Route path="/verify/user/:token" element={<VerifyUser />} />
            <Route path="*" element={<NotFound />} />
          </SentryRoutes>
        </Suspense>
      </BrowserRouter>
    </ConfigProvider>
  );
}
export default AppRoutes;
